/**
 * RioPromptChatDrawer (PROC-34).
 *
 * Prompt-scoped sibling of ``Templates/RioChatDrawer``. Same visual
 * treatment — Clippy-style FAB with /rio.svg, greeting speech bubble,
 * right-side drawer over a backdrop — so Rio looks the same everywhere
 * they show up in basecamp. Chat + Edits tabs mirror the template
 * drawer; Edits surfaces Rio's apply_* tool calls chronologically.
 *
 * Talks to the prompt-scoped Rio chat endpoints:
 *   POST /api/basecamp/prompts/<key>/rio/send
 *   GET  /api/basecamp/prompts/<key>/rio/messages
 */
import { useAuth0 } from "@auth0/auth0-react";
import { PaperAirplaneIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type FC, type KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";

import { Button, Headline, Small } from "@fronterahealth/frontera-ui-components";

import { BASECAMP_API_BASE, apiFetch } from "@api/rest/api-fetch";
import {
  getGetPromptDetailsQueryKey,
  getGetPromptSuggestionQueryKey,
  getListPromptSuggestionTestsQueryKey,
  getListPromptSuggestionsQueryKey,
  useSendRioPromptMessage,
  useSendRioSuggestionMessage,
} from "@api/rest/types-and-hooks";
import { useRioProgress } from "@hooks/useRioProgress";
import { RioAttachButton, type RioAttachment, RioAttachmentChips } from "@pages/Templates/RioAttachmentBar";
import { RioAttachmentPreviewList } from "@pages/Templates/RioAttachmentPreview";
import { RioMessageRole, RioProgressEventType, RioTab, type RioTabValue } from "@pages/Templates/constants";
import { type RioMessage as RioMessageWithEdit } from "@pages/Templates/rioMessages";

import { RioPromptEditsList } from "./RioPromptEditsList";

/**
 * Translate a raw prompt-Rio tool name into a present-continuous
 * status phrase for the awaiting-reply bubble. Mirrors
 * ``humanizeTool`` in the template drawer, scoped to prompt-authoring
 * tools. Unknown tool names fall through to the raw name so the panel
 * is never silently blank.
 */
function humanizePromptTool(toolName: string): string {
  const map: Record<string, string> = {
    // Mutations
    apply_edit_prompt_draft: "Editing draft",
    apply_create_prompt: "Creating prompt",
    apply_delete_prompt_draft: "Deleting draft prompt",
    apply_start_test_run: "Starting test run",
    apply_edit_learner_profile_mock: "Editing profile mock",
    apply_toggle_test_file: "Toggling test file",
    apply_open_edit_prompt_pr: "Opening edit PR",
    apply_open_new_prompt_pr: "Opening new-prompt PR",
    apply_open_edit_prompts_pr: "Opening bulk-edit PR",
    apply_construct_prompt_draft: "Constructing draft",
    // Read tools
    get_prompt_snapshot: "Reading prompt",
    get_draft_overrides: "Reading draft",
    find_matching_prompts: "Searching catalog",
    list_prompts_with_open_drafts: "Listing open drafts",
    list_recent_test_runs: "Reading test runs",
    get_test_run_details: "Reading test run",
    get_learner_test_profile: "Reading profile",
    list_learner_test_profiles: "Listing profiles",
    list_prompts_missing_sample_output: "Finding prompts without sample output",
    list_available_file_kinds: "Reading file kinds",
    // Memory
    get_prompt_memory: "Reading memory",
    record_prompt_preference: "Saving preference",
  };
  return map[toolName] ?? toolName;
}

/**
 * Present-continuous status text for the "Rio is …" bubble. Shows the
 * last progress event's tool name when available, falls back to
 * "Thinking…" otherwise. Same shape the template drawer uses.
 */
function progressStatusText(event: { event: string; tool?: string } | null): string {
  if (!event) return "Thinking…";
  switch (event.event) {
    case RioProgressEventType.TOOL_CALLED:
      return event.tool ? `${humanizePromptTool(event.tool)}…` : "Working…";
    case RioProgressEventType.TOOL_COMPLETED:
      return "Thinking…";
    case RioProgressEventType.MODEL_FINALIZED:
      return "Finalizing reply…";
    default:
      return "Thinking…";
  }
}

// Chat query: USER + ASSISTANT rows only. Namespaced by promptKey so
// switching prompts doesn't share cache state.
const rioPromptChatQueryKey = (promptKey: string, suggestionId?: number) => [
  "rio-prompt-chat-messages",
  promptKey,
  suggestionId ?? null,
];

// Edits query: SYSTEM rows with parsed tool_payload. Separate key so
// the chat poll and the edits panel refresh independently.
const rioPromptEditsQueryKey = (promptKey: string, suggestionId?: number) => [
  "rio-prompt-edit-messages",
  promptKey,
  suggestionId ?? null,
];

// The chat query payload only needs the plain USER/ASSISTANT rows.
// Extra fields on the shared RioMessageWithEdit type (edit / tool_payload)
// are ignored for chat but populated for the Edits tab.
interface RioMessage {
  id: number;
  role: "USER" | "ASSISTANT" | "SYSTEM";
  content: string;
  user_name: string | null;
  message_id: string;
  created_date: string;
  /** Preview payload on USER rows with chat-attached files. */
  attachments?: import("@api/rest/types-and-hooks").RioAttachmentPreview[];
}

interface RioMessageList {
  messages: RioMessage[];
}

interface RioEditMessageList {
  messages: RioMessageWithEdit[];
}

interface PendingTurn {
  // Optimistic placeholder shown while the POST is in flight and while
  // we're polling for Rio's reply. Dropped on error, or superseded by
  // server messages once the reply arrives. message_id is client-generated
  // so React can key on it without colliding with workflow-issued ids.
  message_id: string;
  content: string;
}

interface RioPromptChatDrawerProps {
  promptKey: string;
  promptName: string;
  /** The prompt's own organization, if any -- threaded through to the
   *  attachment upload scope so an image lands in the SAME S3 prefix
   *  Rio's workflow later reads it from. Rio's workflow always mirrors
   *  attachments with the prompt's real organization_id
   *  (rio_prompt_workflow.py), so hardcoding this to null at upload
   *  time silently 404s every attachment on any org-scoped prompt —
   *  the upload and the read land in different S3 prefixes and never
   *  meet (PROC-190). null is only correct for org-less prompts. */
  promptOrgId: string | null;
  /** A prompt suggestion's own Rio thread (PROC-266): Rio edits the
   *  suggestion's draft, never the prompt's. Pass the suggestion's org as
   *  ``promptOrgId`` — Rio mirrors attachments under it. */
  suggestionId?: number;
  /** The thread can be read but not sent to: a suggestion past testing. */
  readOnly?: boolean;
}

export const RioPromptChatDrawer: FC<RioPromptChatDrawerProps> = ({
  promptKey,
  promptName,
  promptOrgId,
  suggestionId,
  readOnly = false,
}) => {
  const { user } = useAuth0();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [pending, setPending] = useState<PendingTurn | null>(null);
  const [attachments, setAttachments] = useState<RioAttachment[]>([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<RioTabValue>(RioTab.CHAT);
  // Cached count of edits at the moment the user switched to the Edits
  // tab. Anything arriving above this threshold while on Chat triggers
  // the unread dot.
  const [lastSeenEditCount, setLastSeenEditCount] = useState(0);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  const qc = useQueryClient();
  const enabled = Boolean(promptKey);
  const threadUrl =
    suggestionId != null
      ? `${BASECAMP_API_BASE}/eval-reviews/suggestions/${suggestionId}/rio`
      : `${BASECAMP_API_BASE}/prompts/${encodeURIComponent(promptKey)}/rio`;

  // Chat query: polls at 2 s only while a user turn is in flight, stops
  // as soon as the reply row lands. Light payload — USER + ASSISTANT only.
  const chatQuery = useQuery<RioMessageList>({
    queryKey: rioPromptChatQueryKey(promptKey, suggestionId),
    queryFn: () => apiFetch<RioMessageList>(`${threadUrl}/messages`),
    enabled,
    staleTime: 0,
    refetchInterval: (q) => {
      const messages = q.state.data?.messages ?? [];
      if (!pending) return false;
      return messages.some((m) => m.message_id === `reply-${pending.message_id}`) ? false : 2000;
    },
  });

  // Edits query: SYSTEM rows with structured tool_payload. Refreshes
  // on demand — piggy-backs on the chat poll transition (reply landed
  // → refetch edits) rather than running its own timer.
  const editsQuery = useQuery<RioEditMessageList>({
    queryKey: rioPromptEditsQueryKey(promptKey, suggestionId),
    queryFn: () => apiFetch<RioEditMessageList>(`${threadUrl}/messages?include_edit_log=true`),
    enabled,
    staleTime: 0,
    refetchInterval: false,
  });

  const sendMutation = useSendRioPromptMessage({
    mutation: {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: rioPromptChatQueryKey(promptKey, suggestionId) });
      },
    },
  });
  const sendSuggestionMutation = useSendRioSuggestionMessage({
    mutation: {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: rioPromptChatQueryKey(promptKey, suggestionId) });
      },
    },
  });

  // Live progress events over the WS. Subscribes to `rio-progress:{promptKey}`
  // on bun-websocket. Events pushed by the pipelines worker's
  // `_rio_prompt_event_stream_handler` show up here so the awaiting-
  // reply bubble can render "Editing draft…" etc. instead of a static
  // "Thinking…". Falls back cleanly when the WS URL isn't configured
  // (hook logs a warning and stays quiet; bubble reads "Thinking…").
  const { latestEvent } = useRioProgress({
    enabled: Boolean(promptKey),
    // A suggestion's session broadcasts on its own channel.
    promptKey: suggestionId != null ? `suggestion-${suggestionId}` : promptKey,
  });

  // Per-turn "displayed event" state. ``latestEvent`` from the hook
  // sticks around across turns — after Rio finishes a turn on
  // ``model_finalized``, the next send would still read that event
  // until a new one arrives, so the awaiting bubble briefly says
  // "Finalizing reply…" for the fresh turn. Track a local per-turn
  // shadow that resets on every new ``pending.message_id`` and only
  // absorbs events emitted after the turn started.
  const [turnEvent, setTurnEvent] = useState<typeof latestEvent>(null);
  useEffect(() => {
    // New turn started (or cleared) → wipe stale event.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setTurnEvent(null);
  }, [pending?.message_id]);
  useEffect(() => {
    // Absorb progress events only while a turn is in flight — anything
    // arriving between turns is ignored (WS is per-channel, not per-turn,
    // so another editor's activity would otherwise leak into our bubble).
    if (latestEvent && pending) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setTurnEvent(latestEvent);
    }
  }, [latestEvent, pending]);

  // Refs so timer callbacks can read current values without stale closures.
  const pendingRef = useRef(pending);
  const awaitingReplyRef = useRef(false);
  useEffect(() => {
    pendingRef.current = pending;
  }, [pending]);

  const allMessages: RioMessage[] = useMemo(() => chatQuery.data?.messages ?? [], [chatQuery.data]);

  const awaitingReply = useMemo(() => {
    if (!pending) return false;
    // Backend contract: assistant reply id is "reply-<user-message-id>".
    return !allMessages.some((m) => m.message_id === `reply-${pending.message_id}`);
  }, [pending, allMessages]);

  useEffect(() => {
    awaitingReplyRef.current = awaitingReply;
  }, [awaitingReply]);

  // NOTE: don't clear ``pending`` when the USER row lands. Doing that
  // flips ``awaitingReply`` false, which (a) stops the 2s chat poll
  // (`refetchInterval` returns false when ``!pending``) so the reply
  // never arrives, and (b) unblocks the composer for a second send
  // while Rio is still working. The optimistic bubble is already
  // hidden via the ``userSaved`` check in the ``chatMessages`` memo
  // once the server row arrives — same pattern as the template
  // drawer. ``pending`` gets overwritten by the next ``setPending``
  // on the following turn, or cleared by the 5-minute backstop.

  // Refresh downstream queries when Rio finishes a turn. Two triggers:
  //   1. WS ``model_finalized`` event — fires as soon as Rio produces
  //      its final answer, BEFORE the ASSISTANT row is persisted. Fast
  //      path so the SPA updates before the poll notices the reply.
  //   2. `awaitingReply` transitions true→false — the polled reply row
  //      landed. Guaranteed backstop even if the WS is down.
  const lastFinalizedRef = useRef<typeof latestEvent>(null);
  const invalidateDownstream = () => {
    // Chat query too: on model_finalized the ASSISTANT row lands
    // slightly after this callback. Invalidating the details query
    // without also invalidating chat means the post-delete auto-
    // redirect on PromptDetail can outrun Rio's reply on the drawer.
    void qc.invalidateQueries({ queryKey: rioPromptChatQueryKey(promptKey, suggestionId) });
    void qc.invalidateQueries({ queryKey: rioPromptEditsQueryKey(promptKey, suggestionId) });
    void qc.invalidateQueries({ queryKey: getGetPromptDetailsQueryKey(promptKey) });
    if (suggestionId != null) {
      // Rio edited the suggestion's draft or started one of its tests.
      void qc.invalidateQueries({ queryKey: getGetPromptSuggestionQueryKey(suggestionId) });
      void qc.invalidateQueries({ queryKey: getListPromptSuggestionTestsQueryKey(suggestionId) });
      void qc.invalidateQueries({ queryKey: getListPromptSuggestionsQueryKey() });
    }
    void qc.invalidateQueries({ queryKey: ["prompt-draft", promptKey] });
    void qc.invalidateQueries({ queryKey: ["prompt-test-runs", promptKey] });
    // Both the plural (list) and singular (detail) profile keys — the
    // profile-editor page uses ``["learner-test-profile", id]`` while
    // list surfaces use ``["learner-test-profiles", ...]``. Prefix
    // matches don't cross the plural/singular boundary.
    void qc.invalidateQueries({ queryKey: ["learner-test-profiles"] });
    void qc.invalidateQueries({ queryKey: ["learner-test-profile"] });
    // Same plural/singular gap for test-run detail views.
    void qc.invalidateQueries({ queryKey: ["prompt-test-run", promptKey] });
  };
  useEffect(() => {
    if (!latestEvent || latestEvent.event !== RioProgressEventType.MODEL_FINALIZED) return;
    if (lastFinalizedRef.current === latestEvent) return;
    lastFinalizedRef.current = latestEvent;
    invalidateDownstream();
    // We can't safely clear pending here — model_finalized is per-channel
    // (not per-turn), so a concurrent editor's finished turn could
    // spuriously unblock our still-in-flight composer. The
    // awaitingReply→false transition below is the authoritative
    // per-turn signal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestEvent, qc, promptKey]);

  const prevAwaitingReplyRef = useRef(false);
  useEffect(() => {
    const was = prevAwaitingReplyRef.current;
    prevAwaitingReplyRef.current = awaitingReply;
    if (was && !awaitingReply) {
      invalidateDownstream();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaitingReply, qc, promptKey]);

  // Backstop: if the reply never arrives, unblock the composer. 20 min —
  // comfortably above one full attempt of the heaviest tool call
  // (apply_construct_prompt_draft's 15-min activity timeout in
  // agent_rio_prompts.py, itself sized for the Constructor's multi-call
  // tool loop) plus the surrounding turn overhead. The old 5-min value
  // fired on legitimately in-progress turns (PROC-119 incident
  // 2026-08-31): the reply arrived correctly 18 minutes in, well after
  // the SPA had already shown "didn't arrive" and freed the composer for
  // a duplicate send that then queued behind the still-working turn.
  useEffect(() => {
    const turnId = pending?.message_id;
    if (!turnId) return;
    const timer = setTimeout(() => {
      if (pendingRef.current?.message_id === turnId && awaitingReplyRef.current) {
        setError("Rio's reply didn't arrive — please try again.");
        setPending(null);
      }
    }, 20 * 60_000);
    return () => clearTimeout(timer);
  }, [pending?.message_id]);

  // Chat view: USER + ASSISTANT rows from the server + optimistic
  // pending bubble for the user's own in-flight turn.
  const chatMessages: Array<RioMessage & { _pending?: boolean }> = useMemo(() => {
    const visible = allMessages.filter((m) => m.role === RioMessageRole.USER || m.role === RioMessageRole.ASSISTANT);
    if (!pending) return visible;
    const userSaved = visible.some((m) => m.message_id === pending.message_id);
    if (userSaved) return visible;
    return [
      ...visible,
      {
        id: -1,
        role: RioMessageRole.USER,
        content: pending.content,
        user_name: user?.name ?? null,
        message_id: pending.message_id,
        created_date: new Date().toISOString(),
        _pending: true,
      },
    ];
  }, [allMessages, pending, user?.name]);

  // Edits tab: SYSTEM rows carrying a parsed `edit` payload only. Rows
  // predating the tool_payload column still come through the fetch but
  // don't have `edit` populated — filter them out so the panel only
  // shows actionable items.
  const editMessages: RioMessageWithEdit[] = useMemo(
    () => (editsQuery.data?.messages ?? []).filter((m) => m.role === RioMessageRole.SYSTEM && m.edit),
    [editsQuery.data],
  );

  // Mark all existing edits as seen the first time data lands (so the
  // dot doesn't trigger on the initial page load), and mark them seen
  // every time the user switches to the Edits tab.
  const firstEditsLoad = useRef(true);
  useEffect(() => {
    if (firstEditsLoad.current && editsQuery.data) {
      firstEditsLoad.current = false;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLastSeenEditCount(editMessages.length);
    }
  }, [editsQuery.data, editMessages.length]);
  useEffect(() => {
    if (activeTab === RioTab.EDITS) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLastSeenEditCount(editMessages.length);
    }
  }, [activeTab, editMessages.length]);
  const unreadEditCount = Math.max(editMessages.length - lastSeenEditCount, 0);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  useEffect(() => {
    if (activeTab !== RioTab.CHAT) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [activeTab, chatMessages.length, pending]);

  const handleSend = () => {
    const content = draft.trim();
    // `uploadingAttachment` guards the ⌘/Ctrl + Enter path — the Send
    // button is already disabled during upload but the shortcut isn't.
    if (!content || awaitingReply || uploadingAttachment) return;
    const localId = crypto.randomUUID().replace(/-/g, "");
    setError(null);
    setPending({ message_id: localId, content });
    setDraft("");
    // Pass localId as the message_id so the workflow uses the same
    // id the client tracks `pending` against. Without this, the
    // server mints its own id and the optimistic placeholder never
    // matches the saved row.
    const data = {
      content,
      message_id: localId,
      display_name: user?.name ?? "",
      ...(attachments.length ? { attachments } : {}),
    };
    const callbacks = {
      onSuccess: (res: unknown) => {
        // customFetch resolves 4xx without throwing, so check explicitly.
        if (res && typeof res === "object" && "status" in res && (res as { status: number }).status >= 400) {
          setError("Failed to send message");
          setPending((p) => (p?.message_id === localId ? null : p));
        } else {
          setAttachments([]);
        }
      },
      onError: (e: unknown) => {
        const msg =
          e && typeof e === "object" && "detail" in e
            ? String((e as { detail: unknown }).detail)
            : "Failed to send message";
        setError(msg);
        setPending((p) => (p?.message_id === localId ? null : p));
      },
    };
    if (suggestionId != null) {
      sendSuggestionMutation.mutate({ suggestionId, data }, callbacks);
    } else {
      sendMutation.mutate({ key: promptKey, data }, callbacks);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      // Typing stays available while Rio works; sending waits for his reply.
      if (!awaitingReply) handleSend();
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      {/* Floating Rio FAB — Clippy-style. Matches Templates/RioChatDrawer's
          position + sizing so Rio looks identical across pages. */}
      <button
        type="button"
        aria-label="Open Rio chat"
        onClick={() => setOpen(true)}
        className={`fixed bottom-12 right-8 z-50 flex h-24 w-24 items-center justify-center transition hover:scale-105 ${
          open ? "pointer-events-none opacity-0" : "opacity-100"
        }`}
      >
        <img src="/rio.svg" alt="" className="h-20 w-20" />
      </button>

      {/* Backdrop */}
      <div
        aria-hidden
        className={`fixed inset-0 z-40 bg-black/10 transition-opacity ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={handleClose}
      />

      {/* Drawer */}
      <aside
        aria-label="Rio chat"
        aria-hidden={!open}
        className={`fixed top-0 right-0 z-50 flex h-screen w-[420px] max-w-[90vw] flex-col border-l border-border-subtle bg-bg-surface shadow-xl transition-transform duration-200 ease-out ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <header className="flex items-start justify-between border-b border-border-subtle px-5 py-4">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/rio.svg" alt="" className="h-8 w-8 shrink-0" />
            <div className="min-w-0">
              <Headline size="small">Rio</Headline>
              <Small className="text-text-secondary truncate">{promptName || promptKey}</Small>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close Rio chat"
            className="rounded p-1 text-text-secondary cursor-pointer hover:bg-bg-default"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </header>

        {/* Tabs — Chat is default. Edits surfaces Rio's structured tool
            log (draft edits, test runs, PR opens) without cluttering
            chat. Layout + active-tab styling match the template Rio
            drawer for cross-surface consistency. */}
        <div className="flex border-b border-border-subtle">
          <button
            type="button"
            onClick={() => setActiveTab(RioTab.CHAT)}
            className={`relative flex-1 py-2.5 text-center text-sm font-medium border-b-2 transition-colors ${
              activeTab === RioTab.CHAT
                ? "border-limestone-900 text-limestone-900"
                : "border-transparent text-text-secondary hover:text-text-primary"
            }`}
          >
            Chat
          </button>
          <button
            type="button"
            onClick={() => setActiveTab(RioTab.EDITS)}
            className={`relative flex-1 py-2.5 text-center text-sm font-medium border-b-2 transition-colors ${
              activeTab === RioTab.EDITS
                ? "border-limestone-900 text-limestone-900"
                : "border-transparent text-text-secondary hover:text-text-primary"
            }`}
          >
            <span className="inline-flex items-center justify-center gap-1.5">
              Edits
              {editMessages.length > 0 && (
                <span className="rounded-full bg-bg-default px-1.5 py-0.5 text-xs text-text-secondary">
                  {editMessages.length}
                </span>
              )}
              {unreadEditCount > 0 && activeTab !== RioTab.EDITS && (
                <span
                  aria-label={`${unreadEditCount} new edit${unreadEditCount === 1 ? "" : "s"}`}
                  className="h-1.5 w-1.5 rounded-full bg-limestone-900"
                />
              )}
            </span>
          </button>
        </div>

        {/* Tab content */}
        {/* Tab content. ``overflow-x-hidden`` + ``min-w-0`` on the
            ``<ul>`` below + ``break-words`` / ``overflow-wrap:anywhere``
            on the prose wrapper prevent long unwrappable tokens (URLs,
            hashes) from triggering a horizontal scrollbar. Same fix
            as the template Rio drawer. */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto overflow-x-hidden px-5 py-4">
          {(chatQuery.isLoading || (activeTab === RioTab.EDITS && editsQuery.isLoading)) && (
            <Small className="text-text-secondary">Loading…</Small>
          )}

          {activeTab === RioTab.CHAT && (
            <>
              {!chatQuery.isLoading && chatMessages.length === 0 && (
                <div className="flex h-full flex-col items-center justify-center text-center">
                  <img src="/rio.svg" alt="" className="h-16 w-16 mb-3 opacity-70" />
                  <Small className="text-text-secondary max-w-[280px]">
                    Start a conversation with Rio about this prompt. Ask for a draft, run tests, edit inputs, or open a
                    PR — Rio can do it.
                  </Small>
                </div>
              )}
              <ul className="flex flex-col gap-3 min-w-0">
                {chatMessages.map((m) => {
                  const isOwnMessage = m.role === RioMessageRole.USER && m.user_name === user?.name;
                  const alignRight = isOwnMessage;
                  const authorLabel = m.user_name;
                  return (
                    <li key={m.message_id} className={`flex ${alignRight ? "justify-end" : "justify-start"}`}>
                      <div className="max-w-[85%]">
                        {m.role === RioMessageRole.ASSISTANT && (
                          <Small className="text-text-secondary mb-0.5 block">Rio</Small>
                        )}
                        {m.role === RioMessageRole.USER && !isOwnMessage && authorLabel && (
                          <Small className="text-text-secondary mb-0.5 block">{authorLabel}</Small>
                        )}
                        <div
                          className={`rounded-lg px-3 py-2 text-sm shadow-sm ${
                            isOwnMessage
                              ? "bg-limestone-100 text-text-primary whitespace-pre-wrap"
                              : m.role === RioMessageRole.USER
                                ? "bg-bg-default text-text-primary whitespace-pre-wrap"
                                : "bg-rio-100 text-text-primary"
                          } ${m._pending ? "opacity-70" : ""}`}
                        >
                          {m.role === RioMessageRole.ASSISTANT ? (
                            // ``components.a`` override matches the template
                            // Rio drawer: open external links in a new tab
                            // (drawer stays put) and give them explicit
                            // color + underline so PR URLs are visibly
                            // clickable against the bubble background.
                            <div className="prose prose-sm max-w-none text-text-primary break-words [overflow-wrap:anywhere] [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_pre]:overflow-x-auto [&_pre]:max-w-full">
                              <ReactMarkdown
                                components={{
                                  a: ({ href, children, ...rest }) => (
                                    <a
                                      {...rest}
                                      href={href}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-rio-600 underline hover:text-rio-700 break-all"
                                    >
                                      {children}
                                    </a>
                                  ),
                                }}
                              >
                                {m.content}
                              </ReactMarkdown>
                            </div>
                          ) : (
                            m.content
                          )}
                          {m.role === RioMessageRole.USER && m.attachments && m.attachments.length > 0 && (
                            <RioAttachmentPreviewList attachments={m.attachments} />
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
                {awaitingReply && (
                  <li className="flex justify-start">
                    <div className="max-w-[85%]">
                      <Small className="text-text-secondary mb-0.5 block">Rio</Small>
                      <div className="rounded-lg bg-rio-100 px-3 py-2 text-sm text-text-secondary shadow-sm">
                        {progressStatusText(turnEvent)}
                      </div>
                    </div>
                  </li>
                )}
              </ul>
            </>
          )}

          {activeTab === RioTab.EDITS && !editsQuery.isLoading && <RioPromptEditsList edits={editMessages} />}
        </div>

        {/* Error banner */}
        {error && (
          <div className="border-t border-border-subtle bg-bg-surface px-5 py-2 text-sm text-text-error-primary">
            {error}
          </div>
        )}

        {/* Composer */}
        {readOnly ? (
          <div className="border-t border-border-subtle px-5 py-3 text-sm text-text-secondary">
            Rio can&apos;t change this suggestion now: it&apos;s shipped, or it needs re-drafting first.
          </div>
        ) : (
          <div className="border-t border-border-subtle px-5 py-3">
            <RioAttachmentChips
              attachments={attachments}
              onRemove={(uuid) => setAttachments((prev) => prev.filter((a) => a.file_uuid !== uuid))}
              disabled={awaitingReply || uploadingAttachment}
            />
            <textarea
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                suggestionId != null
                  ? "Ask Rio to draft, edit, or test this suggestion."
                  : "Ask Rio to draft, edit, test, or open a PR for this prompt."
              }
              rows={3}
              className="w-full resize-none rounded-md border border-border-subtle px-3 py-2 text-sm focus:border-limestone-900 focus:outline-none disabled:opacity-60"
            />
            <div className="mt-2 flex items-center justify-end gap-2">
              <RioAttachButton
                onChange={setAttachments}
                onUploadingChange={setUploadingAttachment}
                scope={{ kind: "prompt", orgId: promptOrgId }}
                disabled={awaitingReply}
              />
              <Button
                text={
                  <span className="inline-flex items-center gap-1.5">
                    Send
                    <PaperAirplaneIcon className="h-4 w-4" />
                  </span>
                }
                appearance="primary"
                onClick={handleSend}
                disabled={!draft.trim() || awaitingReply || uploadingAttachment}
              />
            </div>
          </div>
        )}
      </aside>
    </>
  );
};
